/**
 * Routing and the page shell.
 *
 * ── How the two languages are routed ───────────────────────────────────────
 * Every page is declared once, in PAGES below, and mounted twice: at "/path"
 * for English and "/es/path" for Spanish. Adding a page means one line in that
 * array; adding a language means one more <Route> branch. Nothing else needs to
 * know a second language exists.
 *
 * The paths themselves stay in English under /es (/es/privacy, not
 * /es/privacidad). Translated slugs read slightly better, but they double the
 * route table, break every hard-coded link, and complicate the hreflang pairs
 * for a gain search engines do not reward. Noted here so the choice is visible
 * rather than accidental.
 */
import { Suspense, lazy, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import type { Locale } from '../shared/waitlist';

import { I18nProvider, localeFromPath, useI18n } from './i18n';
import { WaitlistProvider } from './components/WaitlistProvider';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import HomePage from './pages/HomePage';
import NotFoundPage from './pages/NotFoundPage';

/**
 * Legal pages are loaded on demand. They are long, rarely visited, and almost
 * pure text — keeping them out of the initial bundle makes the front page,
 * which is what nearly everyone actually loads, meaningfully smaller.
 */
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const CookiesPage = lazy(() => import('./pages/CookiesPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const DeleteAccountPage = lazy(() => import('./pages/DeleteAccountPage'));

/*
 * Where Supabase's confirmation emails land. Deliberately absent from
 * PAGE_PATHS and sitemap.xml — it is a destination for a link somebody was
 * sent, not a page anybody should find in search.
 */
const AuthConfirmedPage = lazy(() => import('./pages/AuthConfirmedPage'));

const PAGES = [
  { path: '', element: <HomePage /> },
  { path: 'privacy', element: <PrivacyPage /> },
  { path: 'terms', element: <TermsPage /> },
  { path: 'cookies', element: <CookiesPage /> },
  { path: 'support', element: <SupportPage /> },
  { path: 'delete-account', element: <DeleteAccountPage /> },
  { path: 'auth/confirmed', element: <AuthConfirmedPage /> },
];

export default function App() {
  const location = useLocation();
  const locale = localeFromPath(location.pathname);

  return (
    <I18nProvider locale={locale}>
      <DocumentLanguage locale={locale} />
      <ScrollToTop />
      <WaitlistProvider>
        <Shell />
      </WaitlistProvider>
    </I18nProvider>
  );
}

function Shell() {
  const { t } = useI18n();

  return (
    <>
      {/* The first tab stop on the page, for keyboard and screen-reader users. */}
      <a className="skip-link" href="#main">
        {t.nav.skipToContent}
      </a>

      <Header />

      <main id="main">
        <Suspense fallback={<PageLoading />}>
          <Routes>
            {PAGES.map((page) => (
              <Route key={`en-${page.path}`} path={`/${page.path}`} element={page.element} />
            ))}
            {PAGES.map((page) => (
              <Route key={`es-${page.path}`} path={`/es/${page.path}`} element={page.element} />
            ))}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>

      <Footer />
    </>
  );
}

/**
 * Keep <html lang> in step with the route.
 *
 * This is not cosmetic: screen readers choose a pronunciation from it, and
 * getting it wrong makes Spanish read aloud in an English accent. Browsers also
 * use it to offer translation, and search engines to confirm the page's
 * language.
 */
function DocumentLanguage({ locale }: { locale: Locale }) {
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  return null;
}

/**
 * Reset the scroll position on navigation.
 *
 * A single-page app keeps the scroll offset when the route changes, so without
 * this, following a footer link from halfway down the front page lands you
 * halfway down the privacy policy. Anchor links (#faq) are left alone, since
 * for those the offset is the entire point.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname, hash]);

  return null;
}

/** Shown while a lazily-loaded page is fetched. Sized to avoid a layout jump. */
function PageLoading() {
  return <div style={{ minHeight: '60vh' }} aria-hidden="true" />;
}
